// components/SettingsManagement.js
import React, { useState, useEffect } from "react";
import { useAuth } from "components/ui/navigationGuard";
import axiosInstance from "utils/axiosInstance";
import AdminNavigation from "layouts/adminNavigation";
import Button from "components/ui/button";
import Input from "components/ui/input";
import CameraSettings from "components/ui/cameraSettings";
import DirectVideoStream from "components/ui/directVideoStream";
import Notification from "components/ui/notification";

const SettingsManagement = () => {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tools, setTools] = useState([]);
  const [apitools, setAPITools] = useState([]);
  const [activeTab, setActiveTab] = useState("face_recognition");
  const [editedTools, setEditedTools] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingToolId, setEditingToolId] = useState(null);
  const [notification, setNotification] = useState(null);
  const handleToggleCollapse = () => setIsCollapsed(!isCollapsed);
  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
  };

  // Close notification function
  const closeNotification = () => {
    setNotification(null);
  };
  // Fetch face recognition tools
  const fetchTools = async () => {
    try {
      const res = await axiosInstance.get("/face-recognition-tools/getall");
      setTools(res.data);
      setAPITools(res.data.filter(tool => tool.api_key !== 'No' && tool.is_active));

      // Initialize edited tools state
      const initialEditedTools = {};
      res.data.forEach(tool => {
        initialEditedTools[tool.id] = {
          api_key: tool.api_key || "",
          secret_key: tool.secret_key || ""
        };
      });
      setEditedTools(initialEditedTools);
      setHasUnsavedChanges(false);
      setEditingToolId(null); // Reset editing state
    } catch (err) {
      console.error("Failed to fetch face recognition tools", err);
      showNotification("Failed to fetch face recognition tools", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  // Handle service selection with confirmation
  const handleServiceSelect = async (toolId) => {
    const selectedTool = tools.find(t => t.id === toolId);

    // Check if already active
    if (selectedTool.is_active) return;

    // Check for unsaved changes
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to switch services? Your changes will be lost."
      );
      if (!confirmDiscard) return;
    }

    // Confirm service switch
    const confirmSwitch = window.confirm(
      `Are you sure you want to switch to ${selectedTool.display_name}? This will deactivate the current service.`
    );
    if (!confirmSwitch) return;

    try {
      // Update the tool to be active
      await axiosInstance.put(`/face-recognition-tools/update/${toolId}`, {
        ...selectedTool,
        is_active: true
      });

      // Refresh the tools list
      fetchTools();
      showNotification(`Switched to ${selectedTool.display_name} successfully`, "success");
    } catch (err) {
      console.error("Failed to update active tool", err);
      showNotification("Failed to update active tool", "error");
    }
  };

  // Handle API key changes (local state only)
  const handleApiKeyChange = (toolId, field, value) => {
    setEditedTools(prev => ({
      ...prev,
      [toolId]: {
        ...prev[toolId],
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Start editing a tool
  const handleEditClick = (toolId) => {
    setEditingToolId(toolId);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    // Reset to original values
    const resetEditedTools = {};
    tools.forEach(tool => {
      resetEditedTools[tool.id] = {
        api_key: tool.api_key || "",
        secret_key: tool.secret_key || ""
      };
    });
    setEditedTools(resetEditedTools);
    setHasUnsavedChanges(false);
    setEditingToolId(null);
    showNotification("Changes discarded", "info");
  };

  // Save API key changes
  const handleSaveApiKey = async (toolId) => {
    try {
      const tool = tools.find(t => t.id === toolId);
      const editedValues = editedTools[toolId];

      await axiosInstance.put(`/face-recognition-tools/update/${toolId}`, {
        ...tool,
        ...editedValues
      });

      // Refresh the tools list
      fetchTools();
      showNotification("API keys saved successfully", "success");
    } catch (err) {
      console.error("Failed to update tool", err);
      showNotification("Failed to update tool", "error");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation
        user={user}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />

      <main
        className={`pt-16 transition-all duration-200 ease-in-out ${isCollapsed ? "lg:pl-16" : "lg:pl-64"
          }`}
      >
        <div className="container mx-auto mt-10">
          <h2 className="text-2xl font-bold mb-6">
            ⚙️ Camera tool Settings
          </h2>

          {/* Tabs */}
          <div className="flex space-x-4 mb-6">
            {["face_recognition", "camera_settings", "stream"].map((tab) => (
              <button
                key={tab}
                className={`px-4 py-2 rounded ${activeTab === tab
                  ? "bg-primary text-white"
                  : "bg-gray-200 text-gray-700"
                  }`}
                onClick={() => {
                  // If switching tabs while editing, reset editing state
                  if (editingToolId !== null) {
                    if (hasUnsavedChanges) {
                      const confirmDiscard = window.confirm(
                        "You have unsaved changes. Are you sure you want to switch tabs? Your changes will be lost."
                      );
                      if (!confirmDiscard) return;
                    }
                    setEditingToolId(null);
                    setHasUnsavedChanges(false);
                  }
                  setActiveTab(tab);
                }}
              >
                {tab === "face_recognition"
                  ? "Face Recognition"
                  : tab === "camera_settings"
                    ? "Camera Settings"
                    : "Video Stream"}
              </button>
            ))}
          </div>

          {/* Face Recognition Settings */}
          {activeTab === "face_recognition" && (
            <div className="border rounded-lg p-4 mb-6 bg-white shadow-sm">
              {/* <h3 className="font-semibold mb-4">Face Recognition Configuration</h3> */}

              <div className="space-y-6">
                {/* Service Selection with Radio Buttons */}
                <div>
                  <label className="block text-sm font-bold mb-3">Select library for better recognition</label>
                  {/* <div className="flex space-x-6 items-center">
                    {tools.map((tool) => (
                      <div key={tool.id} className="flex items-center">
                        <input
                          type="radio"
                          id={`service-${tool.id}`}
                          name="face-recognition-service"
                          checked={tool.is_active}
                          onChange={() => handleServiceSelect(tool.id)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                        />
                        <label
                          htmlFor={`service-${tool.id}`}
                          className="ml-3 block text-sm font-medium text-gray-700"
                        >
                          {tool.display_name}
                        </label>
                      </div>
                    ))}
                  </div> */}
                  <div className="flex space-x-4 items-center p-2 rounded-xl bg-white">
                    {tools.map((tool) => (
                      <div key={tool.id}>
                        {/* 1. Input remains 'type="radio"' for single-select logic. 
                        2. 'sr-only peer' hides the input but allows the label to be styled based on its state. 
                        */}
                        <input
                          type="radio"
                          id={`service-${tool.id}`}
                          name="face-recognition-service"
                          checked={tool.is_active}
                          onChange={() => handleServiceSelect(tool.id)}
                          className="sr-only peer"
                        />
                        <label
                          htmlFor={`service-${tool.id}`}
                          className={`
                              // Default State (Unselected): Light text, light border, and hover effect
                              cursor-pointer 
                              px-4 py-2 
                              text-sm font-medium 
                              rounded-full 
                              border 
                              border-gray-300 
                              text-gray-600 
                              transition-all 
                              duration-200 
                              shadow-sm
                              hover:bg-gray-50 
                              hover:border-gray-400

                              // Selected State (When peer input is checked): 
                              // Use your 'primary' color for background and border, and set text to white for contrast.
                              peer-checked:bg-primary 
                              peer-checked:text-white 
                              peer-checked:border-primary 
                              peer-checked:shadow-lg
                              peer-checked:shadow-primary/50
                            `}
                        >
                          {tool.display_name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* API Keys Configuration */}
                {apitools.map((tool) => {
                  if (
                    tool.display_name !== "Face++" &&
                    tool.display_name !== "AWS Rekognition"
                  ) {
                    return null;
                  }

                  return (
                    <div
                      key={tool.id}
                      className={`border rounded-lg p-4 ${tool.is_active ? "border-primary bg-blue-50" : "border-gray-200"
                        }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h4
                          className={`font-medium ${tool.is_active ? "text-primary" : "text-gray-700"
                            }`}
                        >
                          {tool.display_name} Configuration
                          {tool.is_active && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                        </h4>

                        {editingToolId !== tool.id ? (
                          <Button
                            onClick={() => handleEditClick(tool.id)}
                            className="px-3 py-1 text-sm"
                          >
                            Edit
                          </Button>
                        ) : (
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => handleSaveApiKey(tool.id)}
                              disabled={!hasUnsavedChanges}
                              className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700"
                            >
                              Save
                            </Button>
                            <Button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 text-sm bg-gray-500 hover:bg-gray-600"
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">API Key</label>
                          <Input
                            placeholder={`Enter ${tool.display_name} API Key`}
                            value={editedTools[tool.id]?.api_key || ""}
                            onChange={(e) =>
                              handleApiKeyChange(tool.id, "api_key", e.target.value)
                            }
                            disabled={editingToolId !== tool.id}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Secret Key
                          </label>
                          <Input
                            placeholder={`Enter ${tool.display_name} Secret Key`}
                            value={editedTools[tool.id]?.secret_key || ""}
                            onChange={(e) =>
                              handleApiKeyChange(tool.id, "secret_key", e.target.value)
                            }
                            type="password"
                            disabled={editingToolId !== tool.id}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

              </div>
            </div>
          )}

          {/* Camera Settings */}
          {activeTab === "camera_settings" && (
            <CameraSettings />
          )}
          {activeTab === "stream" && (
            <DirectVideoStream />
          )}
        </div>
      </main>

      {/* Notification Component */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
    </div>
  );
};

export default SettingsManagement;